import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertCircle,
  Apple,
  Check,
  Copy,
  Loader2,
  Monitor,
  Smartphone,
  TabletSmartphone,
  Terminal,
} from "lucide-react";
import { useState, useEffect } from "react";
import { generateHostsFileContent } from "@/lib/notion-hosts";

const TEST_TARGETS = [
  { ip: "43.128.3.53", url: "https://43.128.3.53.notionfaster.org:8443/file" },
  {
    ip: "101.32.183.34",
    url: "https://101.32.183.34.notionfaster.org:8443/file",
  },
  {
    ip: "119.28.13.121",
    url: "https://119.28.13.121.notionfaster.org:8443/file",
  },
  {
    ip: "198.44.178.123",
    url: "https://198.44.178.123.notionfaster.org:8443/file",
  },
];

const TESTS_PER_IP = 3;
const REQUEST_TIMEOUT = 10000;
const CACHE_KEY_PREFIX = "notion-speed-test";
const CACHE_EXPIRATION_MS = 10 * 60 * 1000;
const CACHE_VERSION = "1.0";

type DeviceType = "windows" | "mac" | "linux" | "android" | "ios";
type TestStatus = "pending" | "testing" | "complete" | "error";
type TestState = "idle" | "testing" | "complete" | "error";

interface TestResult {
  ip: string;
  url: string;
  latencies: number[];
  average: number | null;
  status: TestStatus;
}

interface CachedTestData {
  version: string;
  device: DeviceType;
  timestamp: number;
  testResults: TestResult[];
  testState: TestState;
  expiresAt: number;
}

interface GuideStep {
  text: string;
  image?: string;
  code?: string;
  heading?: boolean;
  link?: string;
  linkText?: string; // Text to display as link (for inline links)
  textBefore?: string; // Text before the inline link
  textAfter?: string; // Text after the inline link
  showHostsContent?: boolean;
}

interface PlatformGuide {
  title: string;
  steps: GuideStep[];
}

const PLATFORM_GUIDES: Record<"windows" | "mac" | "linux", PlatformGuide> = {
  windows: {
    title: "Windows 配置指南",
    steps: [
      {
        text: "本地搜索 cmd，以管理员身份打开",
        image: "/images/guides/windows-cmd.png",
      },
      {
        text: "执行以下命令:",
        code: "notepad C:\\Windows\\System32\\drivers\\etc\\hosts",
      },
      {
        text: "将 host 信息复制粘贴到文件末尾",
        image: "/images/guides/windows-hosts.png",
        showHostsContent: true,
      },
      { text: "保存后退出" },
    ],
  },
  mac: {
    title: "Mac 配置指南",
    steps: [
      {
        text: "方法一：使用 iHosts 应用",
        textBefore: "方法一：使用 ",
        linkText: "iHosts",
        textAfter: " 应用",
        link: "https://apps.apple.com/cn/app/ihosts-etc-hosts-editor/id1102004240?mt=12",
        heading: true,
      },
      {
        text: "将下方 hosts 信息添加到 iHosts",
        image: "/images/guides/mac-ihosts.png",
        showHostsContent: true,
      },
      { text: "" },
      { text: "方法二：使用终端", heading: true },
      { text: "打开终端，执行以下命令:", code: "sudo open /etc/hosts" },
      {
        text: "将 host 内容粘贴到文件末尾",
        image: "/images/guides/terminal.png",
        showHostsContent: true,
      },
      { text: "Ctrl+O 保存,Ctrl+X 退出" },
    ],
  },
  linux: {
    title: "Linux 配置指南",
    steps: [
      { text: "打开终端，执行以下命令:", code: "sudo nano /etc/hosts" },
      {
        text: "将 host 内容粘贴到文件末尾",
        image: "/images/guides/terminal.png",
        showHostsContent: true,
      },
      { text: "Ctrl+O 保存,Ctrl+X 退出" },
    ],
  },
};

function isDesktopPlatform(device: DeviceType): boolean {
  return device === "windows" || device === "mac" || device === "linux";
}

function getCacheKey(device: DeviceType): string {
  return isDesktopPlatform(device)
    ? `${CACHE_KEY_PREFIX}-desktop`
    : `${CACHE_KEY_PREFIX}-${device}`;
}

function isCacheValid(data: CachedTestData): boolean {
  return Date.now() < data.expiresAt && data.testResults.length > 0;
}

function saveToCache(
  device: DeviceType,
  results: TestResult[],
  state: TestState
): void {
  if (state !== "complete" || !isDesktopPlatform(device)) {
    return;
  }

  const now = Date.now();
  const cachedData: CachedTestData = {
    version: CACHE_VERSION,
    device,
    timestamp: now,
    testResults: results,
    testState: state,
    expiresAt: now + CACHE_EXPIRATION_MS,
  };

  try {
    sessionStorage.setItem(getCacheKey(device), JSON.stringify(cachedData));
  } catch (error) {
    console.warn("Failed to save cache:", error);
  }
}

function loadFromCache(device: DeviceType): CachedTestData | null {
  try {
    const cacheKey = getCacheKey(device);
    const cached = sessionStorage.getItem(cacheKey);
    if (!cached) return null;

    const data: CachedTestData = JSON.parse(cached);

    if (data.version !== CACHE_VERSION || !isCacheValid(data)) {
      sessionStorage.removeItem(cacheKey);
      return null;
    }

    return data;
  } catch (error) {
    console.warn("Failed to load cache:", error);
    return null;
  }
}

function clearCache(device?: DeviceType): void {
  try {
    if (device) {
      sessionStorage.removeItem(getCacheKey(device));
    } else {
      sessionStorage.removeItem(`${CACHE_KEY_PREFIX}-desktop`);
    }
  } catch (error) {
    console.warn("Failed to clear cache:", error);
  }
}

export function NotionSpeedTester() {
  const [selectedDevice, setSelectedDevice] = useState<DeviceType>("windows");
  const [testState, setTestState] = useState<TestState>("idle");
  const [testResults, setTestResults] = useState<TestResult[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [copied, setCopied] = useState<boolean>(false);

  useEffect(() => {
    if (!isDesktopPlatform(selectedDevice)) {
      return;
    }

    const cached = loadFromCache(selectedDevice);
    if (cached) {
      setTestResults(cached.testResults);
      setTestState(cached.testState);
      setErrorMessage("");
    } else {
      setTestState("idle");
      setTestResults([]);
      setErrorMessage("");
    }
  }, [selectedDevice]);

  useEffect(() => {
    if (testState === "complete") {
      saveToCache(selectedDevice, testResults, testState);
    }
  }, [testState, testResults, selectedDevice]);

  async function measureLatency(url: string): Promise<number> {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

    try {
      // Add cache-busting query parameters to ensure unique URL per request
      const cacheBustUrl = new URL(url);
      cacheBustUrl.searchParams.set("_t", Date.now().toString());
      cacheBustUrl.searchParams.set(
        "_r",
        Math.random().toString(36).substring(7)
      );

      const start = performance.now();
      const response = await fetch(cacheBustUrl.toString(), {
        method: "GET",
        mode: "cors",
        cache: "no-store", // Changed from "no-cache" - completely bypasses cache
        signal: controller.signal,
      });
      await response.blob();
      const end = performance.now();
      clearTimeout(timeout);
      return end - start;
    } catch (error) {
      clearTimeout(timeout);
      if (error instanceof Error && error.name === "AbortError") {
        throw new Error("请求超时");
      }
      throw new Error(`无法连接到 ${url}`);
    }
  }

  async function runSpeedTests() {
    clearCache(selectedDevice);
    setTestState("testing");
    setErrorMessage("");
    const results: TestResult[] = TEST_TARGETS.map((target) => ({
      ...target,
      latencies: [],
      average: null,
      status: "pending" as TestStatus,
    }));
    setTestResults(results);

    try {
      for (let i = 0; i < TEST_TARGETS.length; i++) {
        const target = TEST_TARGETS[i];
        const latencies: number[] = [];

        results[i].status = "testing";
        setTestResults([...results]);

        for (let testNum = 0; testNum < TESTS_PER_IP; testNum++) {
          try {
            const latency = await measureLatency(target.url);
            latencies.push(latency);
            await new Promise((resolve) => setTimeout(resolve, 100));
          } catch (error) {
            results[i].status = "error";
            setTestResults([...results]);
            throw error;
          }
        }

        const average = latencies.reduce((a, b) => a + b, 0) / latencies.length;
        results[i].latencies = latencies;
        results[i].average = average;
        results[i].status = "complete";
        setTestResults([...results]);
      }

      setTestState("complete");
    } catch (error) {
      setTestState("error");
      setErrorMessage(error instanceof Error ? error.message : "未知错误");
    }
  }

  async function copyToClipboard() {
    const content = generateHostsFileContent(testResults);
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error("复制失败:", error);
    }
  }

  function handleDeviceChange(device: DeviceType) {
    setSelectedDevice(device);
  }

  function renderHostsContentBlock() {
    return (
      <div className="my-3 p-4 border rounded-lg bg-background">
        <div className="relative">
          <Textarea
            readOnly
            value={generateHostsFileContent(testResults)}
            className="font-mono text-sm resize-none pr-12"
            rows={6}
          />
          <Button
            onClick={copyToClipboard}
            variant="ghost"
            size="sm"
            className="absolute top-2 right-2 h-8 w-8 p-0"
          >
            {copied ? (
              <Check className="h-4 w-4" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>
    );
  }

  function renderDesktopContent(platform: "windows" | "mac" | "linux") {
    const guide = PLATFORM_GUIDES[platform];

    if (testState === "idle") {
      return (
        <div className="flex flex-col gap-6 items-center py-8">
          <p className="text-muted-foreground text-center">
            点击下方按钮开始测试，找到最快的 Notion hosts
          </p>
          <Button onClick={runSpeedTests} size="lg">
            开始测速
          </Button>
        </div>
      );
    }

    if (testState === "testing") {
      return (
        <div className="flex flex-col items-center gap-4 py-8">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-lg font-medium">正在测速...</p>
          <p className="text-sm text-muted-foreground">
            正在测试多个 IP，请稍候
          </p>
        </div>
      );
    }

    if (testState === "error") {
      return (
        <div className="flex flex-col gap-6">
          <div className="flex items-start gap-3 p-4 border border-destructive rounded-lg bg-destructive/10">
            <AlertCircle className="h-5 w-5 text-destructive mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-destructive">测速失败</h3>
              <p className="text-sm text-muted-foreground mt-1">
                {errorMessage}
              </p>
            </div>
          </div>
          <Button onClick={runSpeedTests} variant="secondary">
            重试
          </Button>
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-6">
        <div className="p-4 border rounded-lg bg-muted/50 overflow-hidden">
          <ol className="space-y-3 text-sm">
            {guide.steps.map((step, index) => {
              if (!step.text) return <li key={index} className="h-2" />;

              return (
                <li
                  key={index}
                  className={
                    step.heading ? "font-semibold mt-3 mb-1 list-none" : ""
                  }
                >
                  {step.linkText ? (
                    <span>
                      {step.textBefore}
                      <a
                        href={step.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary underline decoration-1 hover:decoration-2 transition-all"
                      >
                        {step.linkText}
                      </a>
                      {step.textAfter}
                    </span>
                  ) : step.link ? (
                    <a
                      href={step.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary underline decoration-1 hover:decoration-2 transition-all"
                    >
                      {step.text}
                    </a>
                  ) : (
                    <span>{step.text}</span>
                  )}

                  {step.showHostsContent && renderHostsContentBlock()}

                  {step.code && (
                    <pre className="mt-2 p-3 bg-background rounded border">
                      <code className="font-mono text-xs whitespace-pre-wrap">
                        {step.code}
                      </code>
                    </pre>
                  )}

                  {step.image && (
                    <img
                      src={step.image}
                      alt={`步骤 ${index + 1}`}
                      className="mt-2 rounded-lg w-full max-w-2xl max-h-96 object-contain mx-auto"
                      onError={(e) => {
                        e.currentTarget.src =
                          "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300'%3E%3Crect width='400' height='300' fill='%23f0f0f0'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' fill='%23999' font-size='14'%3E图片占位符%3C/text%3E%3C/svg%3E";
                      }}
                    />
                  )}
                </li>
              );
            })}
          </ol>
        </div>

        <Button onClick={runSpeedTests} variant="outline">
          重新测速
        </Button>
      </div>
    );
  }

  function renderMobileContent(platform: "android" | "ios") {
    if (platform === "android") {
      return (
        <div className="flex flex-col gap-6 py-4 overflow-hidden">
          <div>
            <h3 className="text-lg font-semibold mb-4">配置加密 DNS</h3>
            <ol className="space-y-3">
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold">
                  1
                </span>
                <span>打开 设置 → 网络和互联网 → 私人 DNS/专用 DNS</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold">
                  2
                </span>
                <span>选择"私人 DNS 提供商主机名"</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold">
                  3
                </span>
                <div className="flex-1">
                  <span className="block mb-2 text-left">输入以下地址:</span>
                  <code className="block font-mono text-sm bg-muted px-3 py-2 rounded">
                    dns.jerryw.cn
                  </code>
                  <img
                    src="/images/guides/android-dns.png"
                    alt="Android DNS 设置"
                    className="mt-2 rounded-lg w-full max-w-2xl max-h-96 object-contain"
                    onError={(e) => {
                      e.currentTarget.src =
                        "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300'%3E%3Crect width='400' height='300' fill='%23f0f0f0'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' fill='%23999' font-size='14'%3E图片占位符%3C/text%3E%3C/svg%3E";
                    }}
                  />
                </div>
              </li>
            </ol>
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-6 py-4 overflow-hidden">
        <div>
          <h3 className="text-lg font-semibold mb-4">配置加密 DNS</h3>
          <ol className="space-y-3 text-sm">
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold">
                1
              </span>
              <div className="flex-1">
                <span className="block mb-2 text-left">下载下面的描述文件</span>
                <Button asChild>
                  <a
                    href="https://dns.jerryw.cn:8443/apple/dot.mobileconfig?host=dns.jerryw.cn"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    下载配置文件
                  </a>
                </Button>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold">
                2
              </span>
              <div className="flex-1">
                <span className="block mb-2 text-left">
                  下载配置文件后，前往「设置」。你将会看到「已下载描述文件选项」。
                </span>
                <img
                  src="/images/guides/ios-settings.png"
                  alt="iOS 设置页面"
                  className="mt-2 rounded-lg w-full max-w-2xl max-h-96 object-contain"
                  onError={(e) => {
                    e.currentTarget.src =
                      "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300'%3E%3Crect width='400' height='300' fill='%23f0f0f0'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' fill='%23999' font-size='14'%3E图片占位符%3C/text%3E%3C/svg%3E";
                  }}
                />
              </div>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold">
                3
              </span>
              <div className="flex-1">
                <span className="block mb-2 text-left">
                  点击以查看刚刚下载的配置文件，选择右上角「安装」文件:
                </span>
                <img
                  src="/images/guides/ios-install.png"
                  alt="iOS 安装配置文件"
                  className="mt-2 rounded-lg w-full max-w-2xl max-h-96 object-contain"
                  onError={(e) => {
                    e.currentTarget.src =
                      "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300'%3E%3Crect width='400' height='300' fill='%23f0f0f0'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' fill='%23999' font-size='14'%3E图片占位符%3C/text%3E%3C/svg%3E";
                  }}
                />
              </div>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold">
                4
              </span>
              <div className="flex-1">
                <span className="block mb-2 text-left">
                  你可以通过「设置 - VPN 与网络 - DNS」设置及选择要使用的 DNS
                  配置。
                </span>
                <img
                  src="/images/guides/ios-dns.png"
                  alt="iOS DNS 设置"
                  className="mt-2 rounded-lg w-full max-w-2xl max-h-96 object-contain"
                  onError={(e) => {
                    e.currentTarget.src =
                      "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300'%3E%3Crect width='400' height='300' fill='%23f0f0f0'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' fill='%23999' font-size='14'%3E图片占位符%3C/text%3E%3C/svg%3E";
                  }}
                />
              </div>
            </li>
          </ol>
        </div>
      </div>
    );
  }

  return (
    <Tabs
      value={selectedDevice}
      onValueChange={(value) => handleDeviceChange(value as DeviceType)}
      className="w-full"
    >
      <TabsList className="grid w-full grid-cols-5">
        <TabsTrigger value="windows" className="flex items-center gap-2">
          <Monitor className="h-4 w-4" />
          <span className="hidden sm:inline">Windows</span>
        </TabsTrigger>
        <TabsTrigger value="mac" className="flex items-center gap-2">
          <Apple className="h-4 w-4" />
          <span className="hidden sm:inline">Mac</span>
        </TabsTrigger>
        <TabsTrigger value="linux" className="flex items-center gap-2">
          <Terminal className="h-4 w-4" />
          <span className="hidden sm:inline">Linux</span>
        </TabsTrigger>
        <TabsTrigger value="android" className="flex items-center gap-2">
          <Smartphone className="h-4 w-4" />
          <span className="hidden sm:inline">Android</span>
        </TabsTrigger>
        <TabsTrigger value="ios" className="flex items-center gap-2">
          <TabletSmartphone className="h-4 w-4" />
          <span className="hidden sm:inline">iOS</span>
        </TabsTrigger>
      </TabsList>

      <TabsContent value="windows">
        {renderDesktopContent("windows")}
      </TabsContent>
      <TabsContent value="mac">{renderDesktopContent("mac")}</TabsContent>
      <TabsContent value="linux">{renderDesktopContent("linux")}</TabsContent>
      <TabsContent value="android">
        {renderMobileContent("android")}
      </TabsContent>
      <TabsContent value="ios">{renderMobileContent("ios")}</TabsContent>
    </Tabs>
  );
}
